import cn from 'clsx'
import Clickable from 'components/Common/Clickable'
import FormatCurrency from 'components/Common/FormatCurrency'
import CardCarousel from 'components/Luxkit/Carousel/CardCarousel'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'
import { TOUR_V2_DEFAULT_OCCUPANCIES } from 'constants/tours'
import { KEYBOARD_MODE_CSS_VAR } from 'contexts/InputModeContext'
import { OfferPageActions, TourV2MonthFilter } from 'contexts/OfferPage/offerPageStateReducer'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { arrayToMap, groupBy, min, sortBy } from 'lib/array/arrayUtils'
import { getMonthShortName } from 'lib/datetime/dateUtils'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { getDepartureOptionsForTravellers } from 'lib/tours/tourDepartureUtils'
import { rem } from 'polished'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'

const Tile = styled(Clickable)`
  padding: ${rem(8)} ${rem(12)};
  text-align: center;
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  height: ${rem(80)};
  min-width: ${rem(104)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  transition: border-color 0.2s, background-color 0.2s;

  &:focus {
    outline: var(${KEYBOARD_MODE_CSS_VAR}, 2px) solid ${props => props.theme.palette.neutral.default.five};
    outline-offset: 1px;
  }

  ${mediaHoverable} {
    &:hover {
      background-color: ${props => props.theme.palette.neutral.default.seven};
    }
  }

  &.selected {
    border: 1px solid ${props => props.theme.palette.neutral.default.one};
  }
`

interface Props {
  departures: Array<Tours.TourV2OfferDeparture>;
  departureYear?: number;
  departureMonth?: TourV2MonthFilter;
  onSelect?: (month: number, year: number) => void;
  travellers?: Array<App.Occupants>;
}

const TourV2DepartureMonthsCarousel = React.forwardRef<HTMLDivElement, Props>((props, ref) => {
  const { departures, departureYear, onSelect, departureMonth, travellers = TOUR_V2_DEFAULT_OCCUPANCIES } = props

  const dispatch = useOfferPageDispatch()
  const monthViews = useMemo(() => {
    const departuresByMonth = groupBy(departures, dep => `${new Date(dep.startDate).getMonth()}-${new Date(dep.startDate).getFullYear()}`)
    const pricesByDeparture = arrayToMap(
      departures,
      departure => departure.id,
      departure => {
        const roomOptions = getDepartureOptionsForTravellers(departure, travellers)
        const cheapestOption = min(roomOptions.map(option => option.option), option => option.memberPrice || option.price)

        if (cheapestOption) {
          return {
            price: cheapestOption.memberPrice || cheapestOption.price,
            type: cheapestOption.memberPrice > 0 ? 'member' : 'default',
          }
        }
        return {
          price: 0,
          type: 'default',
        }
      })

    // Find the cheapest price for this offer so we can let the tile know to highlight it
    const cheapestPrice = min(pricesByDeparture.values().filter(option => !!option.price).map(option => option.price).toArray())

    const months = departuresByMonth.entries().map(([key, departures]) => {
      const date = new Date(departures[0].startDate)
      const departurePrices = departures.map(dep => pricesByDeparture.get(dep.id)).filter(price => !!price?.price)
      const { price, type } = min(departurePrices, price => price!.price) ?? { price: 0, type: 'default' }

      return {
        id: key,
        date,
        year: date.getFullYear(),
        month: date.getMonth(),
        monthLabel: getMonthShortName(date),
        price,
        cheapest: price > 0 && price === cheapestPrice,
        isMemberPrice: type === 'member',
      }
    })

    return sortBy(months, month => new Date(month.date), 'asc')
  }, [departures, travellers])

  const filteredOptions = useMemo(() => {
    if (departureYear) {
      return monthViews.filter(option => option.year === departureYear)
    }
    return monthViews
  }, [departureYear, monthViews])

  const onMonthClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    const month = parseInt(e.currentTarget.dataset.month!)
    const year = parseInt(e.currentTarget.dataset.year!)
    onSelect?.(month, year)

    dispatch({ type: OfferPageActions.setDepartureMonth, month: { month, year } })
  }, [dispatch, onSelect])

  return <CardCarousel gap={12} ref={ref} alignment="center" snap="start" gutterSize={0} gutterStyle="overflow">
    {filteredOptions.map(view => <Tile
      key={view.id}
      onClick={onMonthClick}
      data-year={view.year}
      data-month={view.month}
      className={cn({
        selected: departureMonth?.month === view.month && departureMonth.year === view.year,
      })}
    >
      <BodyText variant="small" colour="neutral-three">{view.year}</BodyText>
      <BodyText variant="large">{view.monthLabel}</BodyText>
      <BodyText variant="large" weight="bold" colour={view.cheapest ? 'highlight-secondary' : view.isMemberPrice ? 'lux-plus' : undefined}>
        {!!view.price && <FormatCurrency value={view.price} format="roundedDollar"/>}
        {!view.price && <Caption variant="small">SOLD OUT</Caption>}
      </BodyText>
    </Tile>)}
  </CardCarousel>
})

TourV2DepartureMonthsCarousel.displayName = 'TourV2DepartureMonthsCarousel'

type MappedProps = Pick<Props, 'travellers' | 'departureMonth' | 'departureYear' | 'departures'>

export default withOfferPageState((state): MappedProps => ({
  departures: state.tourV2Variation!.departures,
  departureYear: state.departureYear,
  travellers: state.rooms,
  departureMonth: state.departureMonth,
}), true)(TourV2DepartureMonthsCarousel)
